import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Paper, Chip,
  Select, MenuItem, FormControl, InputLabel, Alert, IconButton, Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { useDescansoMedicoStore } from '../store/descansoMedicoStore';
import { PeriodoService } from '../../periodos/services/periodoService';
import type { PeriodoPlanilla } from '../../periodos/types/periodo';
import type { DescansoMedico } from '../types/descansoMedico';
import { TIPOS_DESCANSO, ESTADOS_DESCANSO } from '../types/descansoMedico';
import EditDescansoMedico from './EditDescansoMedico';

export default function DescansoMedicoPage() {
  const { descansos, loading, error, fetchByPeriodo, deleteDescanso } = useDescansoMedicoStore();
  const [periodos, setPeriodos] = useState<PeriodoPlanilla[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDescanso, setEditingDescanso] = useState<DescansoMedico | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    loadPeriodos();
  }, []);

  const loadPeriodos = async () => {
    try {
      const data = await PeriodoService.getAll();
      const sorted = data.sort((a, b) => b.anio - a.anio || b.mes - a.mes);
      setPeriodos(sorted);
      if (sorted.length > 0) {
        setSelectedPeriodo(sorted[0].idPeriodo);
      }
    } catch { /* handled by interceptor */ }
  };

  useEffect(() => {
    if (selectedPeriodo) fetchByPeriodo(selectedPeriodo);
  }, [selectedPeriodo]);

  const handleNew = () => {
    setEditingDescanso(null);
    setDialogOpen(true);
  };

  const handleEdit = (row: DescansoMedico) => {
    setEditingDescanso(row);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este descanso médico?')) return;
    try {
      await deleteDescanso(id);
      setMessage('Descanso médico eliminado');
    } catch { /* handled */ }
  };

  const handleDialogClose = (saved?: boolean) => {
    setDialogOpen(false);
    setEditingDescanso(null);
    if (saved) setMessage('Descanso médico guardado correctamente');
  };

  // Resumen totales
  const totalDias = descansos.reduce((s, d) => s + d.diasDescanso, 0);
  const totalSubsidiados = descansos.reduce((s, d) => s + d.diasSubsidiados, 0);
  const totalEmpleador = descansos.reduce((s, d) => s + d.diasEmpleador, 0);

  const columns: GridColDef[] = [
    {
      field: 'acciones', headerName: '', width: 90, sortable: false, filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.idDescansoMedico)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'trabajadorNombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.trabajador?.personal?.personaContacto ?? `#${row.idTrabajador}`,
    },
    {
      field: 'tipoDescanso', headerName: 'Tipo', width: 170,
      renderCell: (p) => {
        const t = TIPOS_DESCANSO.find(td => td.value === p.value);
        return <Chip label={t?.label ?? p.value} size="small" sx={{ bgcolor: t?.color, color: '#fff' }} />;
      },
    },
    {
      field: 'fechaInicio', headerName: 'Desde', width: 110,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'fechaFin', headerName: 'Hasta', width: 110,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '',
    },
    { field: 'diasDescanso', headerName: 'Días', width: 70, type: 'number' },
    {
      field: 'diasEmpleador', headerName: 'Empl.', width: 70, type: 'number',
      description: 'Días pagados por el empleador (máx 20 acumulados/año)',
    },
    {
      field: 'diasSubsidiados', headerName: 'Subs.', width: 70, type: 'number',
      description: 'Días subsidiados por EsSalud (a partir del día 21)',
      renderCell: (p) => p.value > 0 ? <Chip label={p.value} size="small" color="info" /> : '0',
    },
    { field: 'numeroCITT', headerName: 'N° CITT', width: 120 },
    {
      field: 'estado', headerName: 'Estado', width: 120,
      renderCell: (p) => {
        const e = ESTADOS_DESCANSO.find(ed => ed.value === p.value);
        return <Chip label={e?.label ?? p.value} size="small" sx={{ bgcolor: e?.color, color: '#fff' }} />;
      },
    },
    { field: 'diagnostico', headerName: 'Diagnóstico', flex: 1, minWidth: 150 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <HospitalIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Descanso Médico</Typography>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={selectedPeriodo || ''} label="Periodo" onChange={(e) => setSelectedPeriodo(Number(e.target.value))}>
            {periodos.map(p => (
              <MenuItem key={p.idPeriodo} value={p.idPeriodo}>{`${p.mes.toString().padStart(2, '0')}/${p.anio}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => selectedPeriodo && fetchByPeriodo(selectedPeriodo)} disabled={loading}>
          Actualizar
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo Descanso
        </Button>
      </Stack>

      {/* Resumen cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Total Descansos</Typography>
          <Typography variant="h6" fontWeight="bold">{descansos.length}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Total Días</Typography>
          <Typography variant="h6" fontWeight="bold">{totalDias}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Días Empleador</Typography>
          <Typography variant="h6" fontWeight="bold" color="warning.main">{totalEmpleador}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Días Subsidiados</Typography>
          <Typography variant="h6" fontWeight="bold" color="info.main">{totalSubsidiados}</Typography>
        </Paper>
      </Box>

      {/* DataGrid */}
      <Paper sx={{ height: 'calc(100vh - 340px)' }}>
        <DataGrid
          rows={descansos}
          columns={columns}
          getRowId={(row) => row.idDescansoMedico}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          onRowDoubleClick={(params) => handleEdit(params.row as DescansoMedico)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', fontSize: '0.75rem' },
            '& .MuiDataGrid-cell': { fontSize: '0.8rem' },
          }}
        />
      </Paper>

      {/* Dialog */}
      <EditDescansoMedico
        open={dialogOpen}
        onClose={handleDialogClose}
        descanso={editingDescanso}
        idPeriodo={selectedPeriodo}
      />
    </Box>
  );
}
